import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../API/AuthService';
import AdminService from '../../API/AdminService';
import useUserStore from '../../store/userStore';
import usePageMeta from '../../hooks/usePageMeta';
import Spinner from '../../components/Spinner';
import cl from './Admin.module.css';

// Админ-раздел: заявки, центры, счета, сводка.
// Виден только глобальному администратору — бэкенд отдаёт остальным 403,
// здесь проверка нужна лишь чтобы не рисовать заведомо пустой экран.

const TABS = [
    { key: 'summary',  label: 'Сводка' },
    { key: 'requests', label: 'Заявки' },
    { key: 'centres',  label: 'Центры' },
    { key: 'invoices', label: 'Счета' },
];

const PLANS = [
    { key: 'monthly',         label: 'Помесячно — 25 000 ₽' },
    { key: 'yearly_standard', label: 'Год (стандарт) — 150 000 ₽' },
    { key: 'yearly_early',    label: 'Год (раннее) — 100 000 ₽' },
];

const REQUEST_STATUSES = {
    new: 'Новая', in_progress: 'В работе', done: 'Обработана', cancelled: 'Отменена',
};
const CENTRE_STATUSES = { lead: 'Демо', active: 'Оплачен', expired: 'Истёк' };
const INVOICE_STATUSES = {
    draft: 'Черновик', issued: 'Выставлен', paid: 'Оплачен', cancelled: 'Аннулирован',
};

const rub = (kopecks) => ((kopecks || 0) / 100).toLocaleString('ru-RU') + ' ₽';
const dt = (ms) => (ms ? new Date(ms).toLocaleDateString('ru-RU') : '—');

export default function Admin() {
    usePageMeta({ title: 'Администрирование · СтартКласс' });
    const navigate = useNavigate();
    const { profile, loading: userLoading, refresh } = useUserStore();

    const [tab, setTab] = useState('summary');
    const [data, setData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openCentre, setOpenCentre] = useState(null);

    useEffect(() => {
        if (!AuthService.isAuthenticated()) {
            navigate('/login', { replace: true });
            return;
        }
        refresh();
    }, [navigate, refresh]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            if (tab === 'summary')  setData(await AdminService.getSummary());
            if (tab === 'requests') setData(await AdminService.getRequests());
            if (tab === 'centres')  setData(await AdminService.getCentres());
            if (tab === 'invoices') setData(await AdminService.getInvoices());
        } catch (e) {
            setError(e?.response?.status === 403
                ? 'Раздел доступен только администратору'
                : 'Не удалось загрузить данные');
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    // Профиль ещё грузится — ждём, иначе мигнём отказом своему же админу.
    if (userLoading) {
        return <main className={cl.page}><Spinner size="large" text="Загрузка" center/></main>;
    }
    if (profile && profile.role !== 'admin') {
        return (
            <main className={cl.page}>
                <h1 className={cl.title}>Нет доступа</h1>
                <p className={cl.muted}>Этот раздел только для администратора платформы.</p>
            </main>
        );
    }

    return (
        <main className={cl.page}>
            <h1 className={cl.title}>Администрирование</h1>
            <p className={cl.muted}>Заявки, центры, счета и оплаты Старт-класса.</p>

            <div className={cl.tabs}>
                {TABS.map((t) => (
                    <button
                        key={t.key}
                        className={t.key === tab ? cl.tabActive : cl.tab}
                        onClick={() => { setTab(t.key); setOpenCentre(null); }}
                    >{t.label}</button>
                ))}
            </div>

            {error && <div className={cl.error}>{error}</div>}
            {loading && <Spinner size="large" text="Загрузка" center/>}

            {!loading && !error && tab === 'summary'  && <Summary data={data}/>}
            {!loading && !error && tab === 'requests' && <Requests data={data} reload={load}/>}
            {!loading && !error && tab === 'centres'  && (
                <Centres data={data} reload={load} open={openCentre} setOpen={setOpenCentre}/>
            )}
            {!loading && !error && tab === 'invoices' && <Invoices data={data} reload={load}/>}
        </main>
    );
}


function Summary({ data }) {
    const c = data.centres || {};
    const s = data.subscriptions || {};
    const m = data.money || {};
    const r = data.requests || {};
    const p = data.people;           // null → admin-БД недоступна
    const f = data.funnel || {};
    const att = data.attention || {};
    const signups = data.signups_by_month || [];

    const cards = [
        { label: 'Центров всего',      value: c.total || 0 },
        { label: 'Оплачено',           value: c.active || 0, accent: true },
        { label: 'Демо (не оплатили)', value: c.lead || 0 },
        { label: 'Доступ истёк',       value: c.expired || 0 },
        // Учеников считает админ-панель. Если она недоступна, показываем «—»,
        // а не ноль: ноль здесь читался бы как «никого не подключили».
        { label: 'Учеников подключено',
          value: p ? p.students_total : '—' },
        { label: 'Преподавателей',
          value: p ? p.teachers_total : '—' },
        { label: 'Новых заявок',       value: r.new || 0, accent: (r.new || 0) > 0 },
        { label: 'Заканчиваются за 30 дней', value: s.expiring_30d || 0 },
        { label: 'Получено',           value: rub(m.paid_total) },
        { label: 'Ожидает оплаты',     value: rub(m.awaiting_total) },
        { label: 'Дошли до оплаты',
          value: `${f.conversion_pct ?? 0}%`,
          hint: `${f.ever_paid ?? 0} из ${c.total || 0}` },
        { label: 'Ушли после оплаты',  value: f.churn ?? 0 },
    ];

    const maxSignups = Math.max(1, ...signups.map((x) => x.count));

    return (
        <>
            <div className={cl.cards}>
                {cards.map((x) => (
                    <div key={x.label} className={x.accent ? cl.cardAccent : cl.card}>
                        <div className={cl.cardValue}>{x.value}</div>
                        <div className={cl.cardLabel}>{x.label}</div>
                        {x.hint && <div className={cl.cardHint}>{x.hint}</div>}
                    </div>
                ))}
            </div>

            {/* Что требует внимания. Уведомлений на почту нет, поэтому
                увидеть это можно только здесь — числа в карточках выше
                говорят «сколько», а тут видно «у кого». */}
            {(att.expiring?.length > 0 || att.paid_without_students?.length > 0) && (
                <div className={cl.chartCard}>
                    <h3 className={cl.chartTitle}>Требует внимания</h3>

                    {att.expiring?.length > 0 && (
                        <div className={cl.attnGroup}>
                            <div className={cl.attnHead}>
                                Заканчивается доступ ({att.expiring.length})
                            </div>
                            {att.expiring.map((x) => (
                                <div key={`e${x.centre_id}`} className={cl.attnRow}>
                                    <span><b>{x.name || `Центр #${x.centre_id}`}</b>
                                        {x.phone && <span className={cl.small}> · {x.phone}</span>}
                                    </span>
                                    <span className={x.days_left <= 7 ? cl.attnSoon : cl.small}>
                                        через {x.days_left} дн. · {dt(x.expires_at)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {att.paid_without_students?.length > 0 && (
                        <div className={cl.attnGroup}>
                            <div className={cl.attnHead}>
                                Оплатили, но не подключили учеников
                                ({att.paid_without_students.length})
                            </div>
                            {att.paid_without_students.map((x) => (
                                <div key={`p${x.centre_id}`} className={cl.attnRow}>
                                    <span><b>{x.name || `Центр #${x.centre_id}`}</b>
                                        {x.phone && <span className={cl.small}> · {x.phone}</span>}
                                    </span>
                                    <span className={cl.small}>до занятий дело не дошло</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {signups.length > 0 && (
                <div className={cl.chartCard}>
                    <h3 className={cl.chartTitle}>Регистрации по месяцам</h3>
                    <div className={cl.chart}>
                        {signups.map((x) => (
                            <div key={x.month} className={cl.chartCol}>
                                <span className={cl.chartNum}>{x.count}</span>
                                <div className={cl.chartBar}
                                    style={{ height: `${(x.count / maxSignups) * 100}%` }}/>
                                <span className={cl.chartLabel}>{monthLabel(x.month)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}

// «2026-07» → «июл 26»
function monthLabel(key) {
    const [y, m] = String(key).split('-');
    const names = ['янв', 'фев', 'мар', 'апр', 'май', 'июн',
                   'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
    return `${names[Number(m) - 1] || m} ${String(y).slice(2)}`;
}


function Requests({ data, reload }) {
    const rows = data.requests || [];
    if (!rows.length) return <p className={cl.muted}>Заявок пока нет.</p>;

    async function setStatus(id, status) {
        await AdminService.updateRequest(id, { status });
        reload();
    }

    return (
        <table className={cl.table}>
            <thead>
                <tr>
                    <th>Дата</th><th>Тип</th><th>Центр</th><th>Контакт</th>
                    <th>Статус</th><th></th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.id}>
                        <td>{dt(r.created_at)}</td>
                        <td>{r.kind === 'invoice' ? 'Счёт' : 'Звонок'}</td>
                        <td>{r.centre?.name || r.centre_name || '—'}</td>
                        <td>
                            {r.contact_name || '—'}<br/>
                            <span className={cl.small}>{r.contact_email || r.contact_phone || ''}</span>
                        </td>
                        <td><span className={cl.badge}>{REQUEST_STATUSES[r.status] || r.status}</span></td>
                        <td className={cl.actions}>
                            {r.status === 'new' && (
                                <button className={cl.btn} onClick={() => setStatus(r.id, 'in_progress')}>
                                    В работу
                                </button>
                            )}
                            {r.status !== 'done' && (
                                <button className={cl.btn} onClick={() => setStatus(r.id, 'done')}>
                                    Обработана
                                </button>
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}


function Centres({ data, reload, open, setOpen }) {
    const rows = data.centres || [];
    if (!rows.length) return <p className={cl.muted}>Центров пока нет.</p>;
    return (
        <>
            <table className={cl.table}>
                <thead>
                    <tr>
                        <th>Центр</th><th>Город</th><th>Контакт</th>
                        <th>Ученики</th>
                        <th>Статус</th><th>Доступ до</th><th></th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((c) => (
                        <tr key={c.id}>
                            <td><b>{c.name}</b><br/><span className={cl.small}>{c.email || ''}</span></td>
                            <td>{c.city || '—'}</td>
                            <td>{c.contact_person || '—'}<br/>
                                <span className={cl.small}>{c.phone || ''}</span></td>
                            {/* Оплатил, но никого не подключил — повод позвонить:
                                до занятий дело так и не дошло. */}
                            <td>
                                {c.students_used ?? 0} / {c.students_limit || 200}
                                <br/>
                                <span className={cl.small}>
                                    преп. {c.teachers_used ?? 0} / {c.teachers_limit || 5}
                                    {/* Активность считается по Майнкрафтии 2.0.
                                        Большинство учеников пока на старой
                                        платформе, поэтому подписываем источник —
                                        иначе «0 занимаются» читалось бы как
                                        «никто не учится». */}
                                    {c.activity && (
                                        <><br/>в МН2.0 занимаются {c.activity.active}</>
                                    )}
                                </span>
                            </td>
                            <td><span className={cl.badge}>{CENTRE_STATUSES[c.status] || c.status}</span></td>
                            <td>{dt(c.expires_at)}</td>
                            <td className={cl.actions}>
                                <button className={cl.btn} onClick={() => setOpen(c)}>
                                    Выставить счёт
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {open && <InvoiceForm centre={open} onClose={() => setOpen(null)} reload={reload}/>}
        </>
    );
}


function InvoiceForm({ centre, onClose, reload }) {
    const [plan, setPlan] = useState('monthly');
    const [amount, setAmount] = useState('');
    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    async function submit(e) {
        e.preventDefault();
        setBusy(true); setErr('');
        try {
            await AdminService.createInvoice(centre.id, {
                plan,
                amount: amount ? Number(amount) : undefined,
                comment: comment || undefined,
            });
            onClose();
            reload();
        } catch (e2) {
            setErr(e2?.response?.data?.message || 'Не удалось выставить счёт');
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className={cl.overlay} onClick={onClose}>
            <form className={cl.modal} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
                <h2 className={cl.modalTitle}>Счёт для «{centre.name}»</h2>

                <label className={cl.label}>Тариф</label>
                <select className={cl.input} value={plan} onChange={(e) => setPlan(e.target.value)}>
                    {PLANS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>

                <label className={cl.label}>Сумма, ₽ (пусто — из прайса)</label>
                <input className={cl.input} type="number" value={amount}
                       onChange={(e) => setAmount(e.target.value)} placeholder="по прайсу"/>

                <label className={cl.label}>Примечание</label>
                <input className={cl.input} value={comment}
                       onChange={(e) => setComment(e.target.value)}
                       placeholder="скидка, договорённость, номер платёжки"/>

                {err && <div className={cl.error}>{err}</div>}

                <div className={cl.modalActions}>
                    <button type="button" className={cl.btnGhost} onClick={onClose}>Отмена</button>
                    <button type="submit" className={cl.btnPrimary} disabled={busy}>
                        {busy ? 'Выставляю…' : 'Выставить счёт'}
                    </button>
                </div>
            </form>
        </div>
    );
}


function Invoices({ data, reload }) {
    const rows = data.invoices || [];
    if (!rows.length) return <p className={cl.muted}>Счетов пока нет.</p>;

    async function pay(id, number, centreName) {
        // Деньги приходят на расчётный счёт, система об этом не знает —
        // факт оплаты подтверждает человек. Спрашиваем, чтобы доступ не
        // выдался случайным кликом.
        if (!window.confirm(
            `Подтвердить оплату счёта ${number} (${centreName})?\n\n` +
            'Центру откроется доступ на оплаченный период.')) return;
        await AdminService.markPaid(id);
        reload();
    }

    async function cancel(id, number) {
        if (!window.confirm(`Аннулировать счёт ${number}?`)) return;
        await AdminService.cancelInvoice(id);
        reload();
    }

    return (
        <table className={cl.table}>
            <thead>
                <tr>
                    <th>Номер</th><th>Центр</th><th>Сумма</th><th>Тариф</th>
                    <th>Выставлен</th><th>Статус</th><th></th>
                </tr>
            </thead>
            <tbody>
                {rows.map((i) => (
                    <tr key={i.id}>
                        <td><b>{i.number}</b></td>
                        <td>{i.centre?.name || '—'}</td>
                        <td>{rub(i.amount)}</td>
                        <td>{i.plan === 'monthly' ? 'месяц' : 'год'}</td>
                        <td>{dt(i.issued_at)}</td>
                        <td><span className={cl.badge}>{INVOICE_STATUSES[i.status] || i.status}</span></td>
                        <td className={cl.actions}>
                            {i.status === 'issued' && (
                                <>
                                    <button className={cl.btnPrimary}
                                            onClick={() => pay(i.id, i.number, i.centre?.name || '')}>
                                        Оплата поступила
                                    </button>
                                    <button className={cl.btn} onClick={() => cancel(i.id, i.number)}>
                                        Аннулировать
                                    </button>
                                </>
                            )}
                            {i.status === 'paid' && <span className={cl.small}>{dt(i.paid_at)}</span>}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}
